import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Menu, Home, UserCircle, Settings } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { signOut } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ProductSelectionModal } from "@/components/auth/ProductSelectionModal";
import { useUserEnrollments } from "@/hooks/useUserEnrollments";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useCurrentUser, useProfile } from "@/hooks/useProfileQueries";

/**
 * AdminHeader - Product-agnostic header for admin pages.
 * Displays platform branding instead of product branding.
 * Includes "Switch to User Mode" functionality.
 */
export function AdminHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const { enrolledProducts } = useUserEnrollments();
  const { openProfileModal } = useProfileModal();
  const { data: user } = useCurrentUser();
  const { data: profile } = useProfile(user?.id);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleSwitchToUserMode = () => {
    if (enrolledProducts.length === 1) {
      // Single enrollment - go directly to that dashboard
      navigate(`/${enrolledProducts[0]}/dashboard`);
    } else if (enrolledProducts.length > 1) {
      // Multiple enrollments - show product selector
      setShowProductSelector(true);
    } else {
      // No enrollments - go to platform home
      navigate("/");
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md shadow-[0_4px_12px_-2px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.4),0_2px_6px_-2px_rgba(0,0,0,0.3)]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Platform Logo + Admin Title */}
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 dark:from-slate-600 dark:to-slate-800 flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <span className="text-lg font-semibold text-foreground">
                {t("admin.header.platformAdmin", "Platform Admin")}
              </span>
            </Link>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => navigate("/")}
              >
                <Home className="h-4 w-4" />
                {t("admin.header.platformHome", "Platform Home")}
              </Button>

              <Button
                variant="secondary"
                size="sm"
                className="gap-2"
                onClick={handleSwitchToUserMode}
              >
                <UserCircle className="h-4 w-4" />
                {t("admin.header.userMode", "User Mode")}
              </Button>

              <ThemeToggle />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage key={profile?.avatar_url || 'no-avatar'} src={profile?.avatar_url || ''} alt="Profile" />
                      <AvatarFallback className="bg-transparent">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="sr-only">{t("common:nav.profile", "Profile")}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={openProfileModal}>
                    <Settings className="mr-2 h-4 w-4" />
                    {t("common:nav.profile", "Profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSwitchToUserMode}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    {t("admin.header.userMode", "User Mode")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("common:auth.signOut", "Sign Out")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden items-center gap-2">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t("common:nav.menu", "Menu")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="right" className="w-72">
            <SheetHeader>
              <SheetTitle>{t("admin.header.platformAdmin", "Platform Admin")}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-4 mt-6">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  openProfileModal();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <Settings className="h-5 w-5" />
                <span className="font-medium">{t("common:nav.profile", "Profile")}</span>
              </button>

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/");
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <Home className="h-5 w-5" />
                <span className="font-medium">{t("admin.header.platformHome", "Platform Home")}</span>
              </button>

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleSwitchToUserMode();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <UserCircle className="h-5 w-5" />
                <span className="font-medium">{t("admin.header.userMode", "User Mode")}</span>
              </button>

              <div className="border-t border-border my-2" />

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleSignOut();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left text-destructive"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">{t("common:auth.signOut", "Sign Out")}</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Product Selection Modal for switching to user mode */}
      <ProductSelectionModal
        open={showProductSelector}
        onOpenChange={setShowProductSelector}
        enrolledProducts={enrolledProducts}
      />
    </>
  );
}
